// Centre paper stack for the Model-solution authoring view (Navigator main
// area). One white "paper" per confirmed Exam task, in task order — the visual
// equivalent of the stitched reference PDF the backend builds for export.
//
//  * manual / generated / .txt solutions render as prose; the task being edited
//    (Manual button in the card) shows a paper-styled textarea that autosaves on
//    blur via POST /tasks/{id}/model_solution_text.
//  * uploaded PDFs render inline (read-only) via react-pdf.
//  * clicking a task card highlights its paper here with a green rounded frame
//    (useModelUiStore.highlightTaskId); the highlighted paper scrolls into view.
//  * each paper shows its own word counter.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { useTranslation } from "react-i18next";

import { apiJson } from "../api/client";
import { useExamTasks } from "../api/hooks";
import type { ExamTask, ExamTasksResponse, Project } from "../api/types";
import { useModelUiStore } from "../state/modelUiStore";
import { toast } from "../lib/toast";
import "../lib/pdfWorker";
import "./ModelSolutionStack.css";

function wordCount(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

export function ModelSolutionStack({ project }: { project: Project }) {
  const { t } = useTranslation();
  const { data, mutate: mutateTasks } = useExamTasks(project.id);
  const highlightTaskId = useModelUiStore((s) => s.highlightTaskId);

  const tasks = (data?.tasks ?? []).filter((tk) => tk.confirmed);

  // Scroll the highlighted (or just-edited) paper into view.
  const refs = useRef(new Map<number, HTMLDivElement>());
  useEffect(() => {
    if (highlightTaskId == null) return;
    const el = refs.current.get(highlightTaskId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [highlightTaskId]);

  async function refresh(fresh: ExamTasksResponse) {
    await mutateTasks(fresh, { revalidate: false });
  }

  return (
    <div className="model-stack">
      <div className="model-stack__header">
        <span className="nav-main__section-title">{t("navigator.model_solution_preview")}</span>
      </div>
      <div className="model-stack__scroll">
        {tasks.length === 0 ? (
          <div className="model-stack__empty">{t("model_solution_panel.no_tasks")}</div>
        ) : (
          // All tasks share one white paper; each task is a titled section.
          <div className="model-stack__paper">
            {tasks.map((task) => (
              <TaskSection
                key={task.id}
                project={project}
                task={task}
                registerRef={(el) => {
                  if (el) refs.current.set(task.id, el);
                  else refs.current.delete(task.id);
                }}
                onMutated={refresh}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskSection({
  project,
  task,
  registerRef,
  onMutated,
}: {
  project: Project;
  task: ExamTask;
  registerRef: (el: HTMLDivElement | null) => void;
  onMutated: (fresh: ExamTasksResponse) => Promise<void>;
}) {
  const { t } = useTranslation();
  const highlighted = useModelUiStore((s) => s.highlightTaskId === task.id);
  const editing = useModelUiStore((s) => s.editingTaskId === task.id);
  const setEditing = useModelUiStore((s) => s.setEditing);

  const title = task.label || task.operator || task.criterion_key;

  return (
    <section
      ref={registerRef}
      className={"model-stack__section" + (highlighted ? " model-stack__section--highlight" : "")}
    >
      <div className="model-stack__section-head">
        <span className="model-stack__section-title">{title}</span>
        {!editing && (
          <span className="model-stack__section-count">
            {t("paper_review.word_count", { count: task.model_solution_words ?? 0 })}
          </span>
        )}
      </div>
      <div className="model-stack__section-body">
        {editing ? (
          <SolutionEditor
            project={project}
            task={task}
            onDone={() => setEditing(null)}
            onMutated={onMutated}
          />
        ) : task.model_solution_kind === "pdf" ? (
          <PdfInline url={`/api/projects/${project.id}/tasks/${task.id}/model_solution_pdf`} />
        ) : task.model_solution_text ? (
          <Prose text={task.model_solution_text} />
        ) : (
          <div className="model-stack__placeholder">
            {t("model_solution_panel.paper_empty")}
          </div>
        )}
      </div>
    </section>
  );
}

function Prose({ text }: { text: string }) {
  const paragraphs = text.split(/\n{2,}/).filter(Boolean);
  return (
    <div className="model-stack__prose">
      {paragraphs.map((para, i) => (
        <p key={i} className="model-stack__para">
          {para.split("\n").map((line, j, arr) =>
            j < arr.length - 1 ? <span key={j}>{line}<br /></span> : line,
          )}
        </p>
      ))}
    </div>
  );
}

function SolutionEditor({
  project,
  task,
  onDone,
  onMutated,
}: {
  project: Project;
  task: ExamTask;
  onDone: () => void;
  onMutated: (fresh: ExamTasksResponse) => Promise<void>;
}) {
  const { t } = useTranslation();
  // PDF-backed solutions have no editable text — Manual starts from a blank page.
  const initial = task.model_solution_kind === "pdf" ? "" : (task.model_solution_text ?? "");
  const [text, setText] = useState(initial);
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el) {
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, []);

  async function save() {
    if (saving) return;
    if (text === initial) {
      onDone();
      return;
    }
    setSaving(true);
    try {
      const fresh = await apiJson<ExamTasksResponse>(
        `/api/projects/${project.id}/tasks/${task.id}/model_solution_text`,
        { method: "POST", body: JSON.stringify({ text }) },
      );
      await onMutated(fresh);
      toast(t("model_solution_panel.saved"));
    } catch {
      toast(t("model_solution_panel.save_failed"));
    } finally {
      setSaving(false);
      onDone();
    }
  }

  return (
    <div className="model-stack__editor">
      <textarea
        ref={ref}
        className="model-stack__textarea"
        value={text}
        placeholder={t("model_solution_panel.paste_placeholder")}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => void save()}
        disabled={saving}
        spellCheck={false}
      />
      <div className="model-stack__editor-hint">
        {saving
          ? t("model_solution_panel.saving")
          : t("paper_review.word_count", { count: wordCount(text) })}
      </div>
    </div>
  );
}

function PdfInline({ url }: { url: string }) {
  const [pdfData, setPdfData] = useState<{ data: ArrayBuffer } | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [width, setWidth] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(url, { credentials: "include" })
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject()))
      .then((buf) => { if (!cancelled) setPdfData({ data: buf }); })
      .catch(() => {});
    return () => { cancelled = true; setPdfData(null); };
  }, [url]);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="model-stack__pdf" ref={wrapRef}>
      <Document
        file={pdfData ?? undefined}
        loading={<div className="model-stack__pdf-loading" />}
        error={null}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
      >
        {width > 0 &&
          Array.from({ length: numPages }, (_, i) => (
            <Page
              key={i}
              pageNumber={i + 1}
              width={width}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          ))}
      </Document>
    </div>
  );
}
